import { S3UploadType, Task } from '@admiin-com/ds-graphql';
import React from 'react';
import { useDocumentUrl } from '../../hooks/useDocumentUrl/useDocumentUrl';
import { WBBox, WBButton, WBImage } from '@admiin-com/ds-web';
import PdfViewModal from '../PdfViewModal/PdfViewModal';
import { createCustomSignatureNode } from '../../helpers/signature';
import { AnnotationsUnion } from '@nutrient-sdk/viewer';
import PdfPlaceholder from '../PdfPlaceholder/PdfPlaceholder';
import { useResizeObserver } from '@admiin-com/ds-hooks';
import { getNutrientConfig } from '../../features/hooks/useNutrientPreload/useNutrientPreload';

export interface PdfThumbnailProps {
  documentUrl: string;
  isImage: boolean;
}
const renderConfigurations: Record<any, any> = {};

export const PdfThumbnail = React.forwardRef(
  (props: PdfThumbnailProps, instanceRef: any) => {
    const { documentUrl, isImage } = props;
    const [image, setImage] = React.useState<string>('');
    const [containerRef, { width, height }] = useResizeObserver();

    React.useEffect(() => {
      const container = containerRef.current; // This `useRef` instance will render the PDF.
      let NutrientViewer: any;
      const fetchThumbnail = async () => {
        const getAnnotationRenderers = ({
          annotation,
        }: {
          annotation: AnnotationsUnion;
        }) => {
          // Use cached render configuration
          if (renderConfigurations[annotation.id]) {
            return renderConfigurations[annotation.id];
          }

          const { customData } = annotation;
          const placeholderUi = <PdfPlaceholder customData={customData} />;
          const customNode = createCustomSignatureNode(
            placeholderUi,
            customData
          );

          renderConfigurations[annotation.id] = {
            node: customNode,
            append: true,
          };
          return renderConfigurations[annotation.id] || null;
        };
        if (documentUrl) {
          NutrientViewer = await import('@nutrient-sdk/viewer');
          NutrientViewer.unload(container); // Ensure that there's only one Nutrient Viewer instance.

          const instance = await NutrientViewer.load({
            container,
            document: documentUrl,
            ...getNutrientConfig(),
            toolbarItems: [],
            disableTextSelection: false,
            headless: true,
            customRenderers: {
              Annotation: getAnnotationRenderers,
            },
            styleSheets: ['/viewer.css'],
            // instantJSON: annotations ? JSON.parse(annotations) : null,
          });
          // Renders the first page (page index 0).
          const src = await instance.renderPageAsImageURL({ width: 400 }, 0);
          // instanceRef.current = instance;
          setImage(src);
        }
      };
      fetchThumbnail();
      return () => {
        if (NutrientViewer) {
          NutrientViewer.unload(container);
        }
      };
    }, [documentUrl]);

    React.useEffect(() => {
      if (isImage) {
        setImage(documentUrl);
      }
    }, [isImage, documentUrl]);
    const [open, setOpen] = React.useState<boolean>(false);
    return (
      <>
        <WBBox ref={containerRef} />
        <WBButton
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          sx={{
            padding: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'transparent',
          }}
        >
          {image && (
            <WBImage
              onClick={(e) => {
                e.stopPropagation();
                setOpen(true);
              }}
              stretchBackground={true}
              src={image}
              sx={{ maxWidth: '100%' }}
            />
          )}
        </WBButton>
        <PdfViewModal
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          documentUrl={documentUrl}
          isImage={isImage}
        />
      </>
    );
  }
);

export default PdfThumbnail;
